import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Box,
  Flex,
  Icon,
  Divider,
  Portal,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
// import { CheckboxField } from '@/components/formBuilder/formFields/CheckboxField';
import { Dragsvg, Settings01 } from '@kula-ai/icons-core';
import {
  DragDropContext,
  Droppable,
  Draggable,
} from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration';
import { KText } from '@/ui-library/components/text';
import { isEqual } from 'lodash-es';
import { Checkbox } from '@kula-ai/components';

/**
 * TableColumnSettings Component
 *
 * Manages column visibility and reordering for tables with configurable columns.
 *
 * COLUMN CONFIG VALUES:
 *
 * • false (boolean)
 *   - Non-configurable columns (e.g., Name, row actions)
 *   - Hidden from column settings UI completely
 *   - Cannot be reordered or hidden
 *   - Use case: Essential columns that must always be visible
 *
 * • true (boolean)
 *   - Standard configurable columns
 *   - Show in column settings UI with full controls
 *   - Can be reordered and hidden/shown
 *   - Use case: Regular table columns
 *
 * • 'locked' (string)
 *   - No reordering allowed
 *   - No visibility toggle (always visible)
 *   - Shows in UI but disabled
 *   - Use case: Important columns that should stay in place (e.g., Time column)
 *
 * • 'skipreorder' (string)
 *   - No reordering allowed (no drag handle)
 *   - Visibility toggle enabled
 *   - Can be hidden/shown but position is fixed
 *   - Use case: Columns that need fixed positioning but optional visibility (e.g., Stage columns)
 *
 * • 'hidden' (string)
 *   - Completely hidden from column settings UI
 *   - Excluded from "last visible column" count
 *   - Can still be controlled programmatically via relatedColumns
 *   - Use case: Auto-managed columns (e.g., percentage columns that hide with their parent)
 *
 * RELATED COLUMNS:
 *
 * • relatedColumns: string[]
 *   - Array of column IDs that should auto-hide when this column is hidden
 *   - Only works when hideReorderColumns prop is true
 *   - Use case: Stage columns that should hide their associated percentage columns
 */

interface Column {
  id: string;
  title: string;
  columnConfig?: boolean | string;
  columns?: Column[]; // For grouped columns
  Header?: string; // Alternative to title for grouped columns
  accessor?: string; // Fallback ID for columns without explicit id
}

interface TableColumnSettingsProps {
  columns: Column[];
  allColumns?: Column[]; // Flattened columns from react-table
  setColumnOrder: (_order: string[]) => void;
  toggleHideColumn: (_columnId: string, _isHidden: boolean) => void;
  setHiddenColumns: (_columns) => void;
  tableId: string;
  addDefaultColumnsAtTop?: boolean;
  tableColStyles?: any;
  onTableColDataChange?: (_tableColData: {
    order: string[];
    visibility: Record<string, boolean>;
  }) => void;
  initialTableColData?: {
    order: string[];
    visibility: Record<string, boolean>;
  };
  skipLocalStorage?: boolean;
}

const getStoredSettings = (tableId: string) => {
  const storedSettings = localStorage.getItem(`tableColSettings_${tableId}`);
  return storedSettings ? JSON.parse(storedSettings) : {};
};

export const TableColumnSettings = ({
  columns = [],
  setColumnOrder = () => {},
  setHiddenColumns = () => {},
  tableId,
  addDefaultColumnsAtTop = false,
  tableColStyles = {},
  onTableColDataChange,
  initialTableColData,
  skipLocalStorage = false,
}: TableColumnSettingsProps) => {
  const [config, setConfig] = useState({
    visibility: {},
    order: [],
  });

  const { isOpen, onOpen, onClose } = useDisclosure();

  // Memoize default column IDs to prevent recalculation
  const defaultColumnIds = useMemo(() => {
    const defaultIds = columns
      .filter((col) => col.columnConfig === false)
      .map((col) => col.id || col.accessor)
      .filter(Boolean);
    return defaultIds;
  }, [columns]);

  // Helper function to identify if a column is a group
  const isGroupColumn = (column: Column): boolean => {
    return !!(column.columns && column.columns.length > 0);
  };

  // Helper function to get configurable columns (excludes groups, shows only top-level)
  const getConfigurableColumns = useMemo(() => {
    const configurableColumns = columns.filter(
      (col) => col.columnConfig !== false
    );

    return configurableColumns;
  }, [columns]);

  // Helper function to count visible configurable columns (excludes hidden columns from UI)
  const countVisibleColumns = useMemo(() => {
    let visibleCount = 0;

    getConfigurableColumns.forEach((column) => {
      const columnId = column.id || column.accessor;
      const isVisible = columnId && config.visibility[columnId] !== false;

      // Exclude columns that are hidden from UI (like percentage columns)
      const isHiddenFromUI = column.columnConfig === 'hidden';

      // Exclude locked columns from the count (they should always be visible)
      const isLocked = column.columnConfig === 'locked';

      if (isVisible && !isHiddenFromUI && !isLocked) {
        visibleCount++;
      }
    });

    return visibleCount;
  }, [getConfigurableColumns, config.visibility]);

  // Helper function to get configurable columns in the correct order
  const getOrderedConfigurableColumns = useMemo(() => {
    if (!config.order || config.order.length === 0) {
      return getConfigurableColumns;
    }

    // Create a map for quick lookup
    const columnMap = new Map();
    getConfigurableColumns.forEach((col) => {
      const colId = col.id || col.accessor;
      if (colId) {
        columnMap.set(colId, col);
      }
      // Also map sub-columns to their parent group
      if (isGroupColumn(col)) {
        col.columns!.forEach((subCol) => {
          const subColId = subCol.id || subCol.accessor;
          if (subColId) {
            columnMap.set(subColId, col);
          }
        });
      }
    });

    // Build ordered list based on config.order
    const orderedColumns = [];
    const addedColumnIds = new Set();

    config.order.forEach((columnId) => {
      // Skip null, undefined, or invalid column IDs
      if (!columnId || columnId === 'null' || columnId === 'undefined') {
        return;
      }

      const column = columnMap.get(columnId);
      if (column) {
        const actualColumnId = column.id || column.accessor;
        if (
          !addedColumnIds.has(actualColumnId) &&
          column.columnConfig !== false
        ) {
          orderedColumns.push(column);
          addedColumnIds.add(actualColumnId);
        }
      }
    });

    // Add any remaining configurable columns that weren't in the order
    getConfigurableColumns.forEach((col) => {
      const colId = col.id || col.accessor;
      if (colId && !addedColumnIds.has(colId)) {
        orderedColumns.push(col);
      }
    });

    return orderedColumns;
  }, [getConfigurableColumns, config.order]);

  useEffect(() => {
    if (!tableId) return;

    // Clean up any corrupted data from previous state
    setConfig((prevConfig) => {
      const cleanVisibility = { ...prevConfig.visibility };
      if (Object.prototype.hasOwnProperty.call(cleanVisibility, 'undefined')) {
        delete cleanVisibility['undefined'];
      }

      // Clean up corrupted order array
      const cleanOrder = prevConfig.order.filter(
        (columnId) =>
          columnId && columnId !== 'null' && columnId !== 'undefined'
      );

      return {
        order: cleanOrder,
        visibility: cleanVisibility,
      };
    });

    let finalVisibility = {};
    let finalOrder = [];

    // Priority: initialTableColData > localStorage (if not skipped) > defaults
    const { order: localOrder, visibility: localVisibility } =
      initialTableColData ||
      (!skipLocalStorage ? getStoredSettings(tableId) : {}) ||
      {};

    // Get all column IDs including sub-columns for order management
    const getAllColumnIds = () => {
      const allIds: string[] = [];
      columns.forEach((col) => {
        if (isGroupColumn(col)) {
          // This is a grouped column, add all sub-column IDs
          col.columns!.forEach((subCol) => {
            // Use id if available, otherwise fall back to accessor
            const columnId = subCol.id || subCol.accessor;
            if (columnId) allIds.push(columnId);
          });
        } else {
          // Regular column
          const colId = col.id || col.accessor;
          if (colId) allIds.push(colId);
        }
      });
      return allIds;
    };

    const allColumnIds = getAllColumnIds();

    //check local order or set default order
    if (localOrder && localOrder?.length > 0) {
      // Check if there are any new columns that aren't in the saved order
      const savedColumnIds = new Set(localOrder);
      const newColumnIds = allColumnIds.filter((id) => !savedColumnIds.has(id));

      // Add any new columns to the end of the order
      finalOrder = [...localOrder, ...newColumnIds];
    } else {
      finalOrder = allColumnIds;
    }

    if (localVisibility && Object.keys(localVisibility).length > 0) {
      // Handle visibility for both regular and grouped columns
      finalVisibility = {};

      columns.forEach((col) => {
        if (isGroupColumn(col)) {
          // This is a grouped column - check group visibility and propagate to sub-columns
          const colId = col.id || col.accessor;
          const groupVisible = Object.prototype.hasOwnProperty.call(
            localVisibility,
            colId
          )
            ? localVisibility[colId]
            : true;

          if (colId) {
            finalVisibility[colId] = groupVisible;
          }

          // Set all sub-columns to the same visibility as the group
          col.columns!.forEach((subCol) => {
            const columnId = subCol.id || subCol.accessor;
            if (columnId) finalVisibility[columnId] = groupVisible;
          });
        } else {
          // Regular column
          const colId = col.id || col.accessor;
          if (colId) {
            finalVisibility[colId] = Object.prototype.hasOwnProperty.call(
              localVisibility,
              colId
            )
              ? localVisibility[colId]
              : true;
          }
        }
      });
    } else {
      // If no saved visibility, set all columns to visible
      finalVisibility = {};
      columns.forEach((col) => {
        if (isGroupColumn(col)) {
          // Grouped column
          const colId = col.id || col.accessor;
          if (colId) {
            finalVisibility[colId] = true;
          }
          col.columns!.forEach((subCol) => {
            const columnId = subCol.id || subCol.accessor;
            if (columnId) finalVisibility[columnId] = true;
          });
        } else {
          // Regular column
          const colId = col.id || col.accessor;
          if (colId) {
            finalVisibility[colId] = true;
          }
        }
      });
    }

    const filteredFinalOrder = finalOrder.filter(
      (id) => !defaultColumnIds.includes(id)
    );

    const newConfig = {
      order: addDefaultColumnsAtTop
        ? [...defaultColumnIds, ...filteredFinalOrder]
        : finalOrder,
      visibility: finalVisibility,
    };

    // Only update if config has changed to prevent unnecessary re-renders
    if (!isEqual(newConfig, config)) {
      setConfig(newConfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addDefaultColumnsAtTop,
    columns,
    tableId,
    defaultColumnIds,
    initialTableColData,
    skipLocalStorage,
  ]);

  // Save settings and update table when config changes
  useEffect(() => {
    const { visibility, order } = config;

    // Skip if order is empty (initial render)
    if (!order.length) return;

    //update local data (only if not skipping localStorage)
    if (!skipLocalStorage) {
      localStorage.setItem(
        `tableColSettings_${tableId}`,
        JSON.stringify({
          order: order,
          visibility: visibility,
        })
      );
    }

    //update table visibility
    setColumnOrder(order);

    //update table order
    const falseKeys = Object.keys(visibility).filter((key) => !visibility[key]);
    setHiddenColumns(falseKeys);

    // Notify parent component of column data changes
  }, [config, setColumnOrder, setHiddenColumns, skipLocalStorage, tableId]);

  const onDragEnd = useCallback(
    (result: any) => {
      if (!result.destination) return;
      const startIndex = result.source.index;
      const endIndex = result.destination.index;

      // Get only the configurable columns (those that can be reordered) in the correct order
      const configurableColumns = getOrderedConfigurableColumns;

      // Create a new array with the reordered configurable columns
      const newConfigurableOrder = Array.from(configurableColumns);
      const [movedColumn] = newConfigurableOrder.splice(startIndex, 1);
      newConfigurableOrder.splice(endIndex, 0, movedColumn);

      // Create the new order by expanding groups to their sub-columns
      const expandColumnToIds = (column: Column) => {
        if (isGroupColumn(column)) {
          // This is a grouped column, return all sub-column IDs
          return column
            .columns!.map((subCol) => subCol.id || subCol.accessor)
            .filter(Boolean);
        } else {
          // Regular column
          const colId = column.id || column.accessor;
          return colId ? [colId] : [];
        }
      };

      // Build the new order with proper sub-column expansion
      const expandedNewOrder = [];

      // Get the expanded IDs for the new configurable order
      const newConfigurableExpandedIds = [];
      newConfigurableOrder.forEach((col) => {
        const expandedIds = expandColumnToIds(col);
        newConfigurableExpandedIds.push(...expandedIds);
      });

      // Simplified approach: Replace the entire order with the new expanded order
      // First add non-configurable columns (like header)
      const nonConfigurableIds = [];

      // Find non-configurable columns from original order
      config.order.forEach((columnId) => {
        const originalColumn = columns.find((col) => {
          if (isGroupColumn(col)) {
            // Check if this ID belongs to any sub-column of a group
            return col.columns!.some(
              (subCol) => (subCol.id || subCol.accessor) === columnId
            );
          }
          return (col.id || col.accessor) === columnId;
        });

        // If column not found or is non-configurable, keep it
        if (!originalColumn || originalColumn.columnConfig === false) {
          nonConfigurableIds.push(columnId);
        }
      });

      // Build final order: non-configurable columns + new configurable order

      expandedNewOrder.push(...nonConfigurableIds);
      expandedNewOrder.push(...newConfigurableExpandedIds);

      // Create new config with updated order
      const newConfig = {
        order: expandedNewOrder,
        visibility: config.visibility,
      };

      // Only update if config has changed to prevent unnecessary re-renders
      if (!isEqual(newConfig, config)) {
        setConfig(newConfig);
        onTableColDataChange?.(newConfig);
      }
    },
    [columns, config, onTableColDataChange, getOrderedConfigurableColumns]
  );

  const getDraggableStyle = useCallback((provided: any, snapshot: any) => {
    // Only return positioning styles from react-beautiful-dnd.
    // Visual styles (background, border, shadow) are applied via sx prop
    // to avoid Chakra v2 Emotion specificity conflicts.
    return {
      ...provided.draggableProps.style,
      ...(snapshot?.isDragging && { zIndex: 99999 }),
    };
  }, []);

  const getDraggableSx = useCallback((snapshot: any) => {
    if (snapshot.isDragging) {
      return {
        boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
        border: '2px solid #605AFB',
        borderRadius: '6px',
        background: 'var(--kula-colors-white, #fff)',
        // Above popover (ensure drag preview stays on top of PopoverContent)
        zIndex: 99999,
      };
    }
    return {};
  }, []);

  const getDroppableStyles = useCallback((isDraggingOver: boolean) => {
    if (isDraggingOver)
      return {
        background: 'var(--kula-dark-secondary-25, #F4F4FB)',
        border: '1px solid #605AFB',
        borderRadius: '6px',
        padding: '.25em',
      };
    return {};
  }, []);

  const handleColumnVisibilityChange = useCallback(
    (columnId: string, isVisible: boolean) => {
      const column = columns.find(
        (col) => (col.id || col.accessor) === columnId
      );

      const newVisibility = { ...config.visibility };

      if (column && isGroupColumn(column)) {
        // This is a grouped column - update group and all sub-columns
        newVisibility[columnId] = isVisible;
        column.columns!.forEach((subCol) => {
          const subColId = subCol.id || subCol.accessor;
          if (subColId) newVisibility[subColId] = isVisible;
        });
      } else {
        // Regular column - only set if columnId is valid
        if (columnId && columnId !== 'undefined') {
          newVisibility[columnId] = isVisible;
        }

        // Auto-hide related columns based on column configuration
        if (tableColStyles?.hideReorderColumns) {
          const column = columns.find(
            (col) => (col.id || col.accessor) === columnId
          );
          if ((column as any)?.relatedColumns) {
            (column as any).relatedColumns.forEach((relatedId: string) => {
              newVisibility[relatedId] = isVisible;
            });
          }
        }
      }

      // Only update if visibility has changed
      if (config.visibility[columnId] !== isVisible) {
        setConfig((prev) => {
          const newConfig = {
            order: prev.order,
            visibility: newVisibility,
          };
          onTableColDataChange?.(newConfig);
          return newConfig;
        });
      }
    },
    [config.visibility, onTableColDataChange, columns, tableColStyles]
  );

  // Memoize the column settings render to prevent unnecessary re-renders
  const renderColumnSettings = useCallback(() => {
    // Get only configurable columns (this now includes groups) in the correct order
    const configurableColumns = getOrderedConfigurableColumns;

    // Filter out percentage columns for the pipeline table
    const filteredColumns = configurableColumns.filter((column) => {
      const id = column.id || column.accessor;

      // Skip columns without valid id/accessor
      if (!id) {
        return false;
      }

      // Skip columns marked as 'hidden' in columnConfig
      if (column.columnConfig === 'hidden') {
        return false;
      }

      return true;
    });

    const isReorderDisabled = tableColStyles?.hideReorderColumns;

    const renderRow = (column: any, provided: any, snapshot: any) => {
      const { title = '', Header = '', columns: subColumns } = column;
      const id = column.id || column.accessor;
      const displayTitle = title || Header;
      const isGroup = isGroupColumn(column);
      return (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={isReorderDisabled ? {} : getDraggableStyle(provided, snapshot)}
          sx={isReorderDisabled ? undefined : getDraggableSx(snapshot)}
        >
          <Flex p="12px 6px" gap="6px">
            {!isReorderDisabled &&
              column.columnConfig !== 'skipreorder' &&
              column.columnConfig !== 'locked' && (
                <Box
                  {...provided.dragHandleProps}
                  cursor={snapshot.isDragging ? 'grabbing' : 'grab'}
                >
                  <Dragsvg size={16} className="mt-0.5" />
                </Box>
              )}
            <Checkbox
              checked={config?.visibility?.[id]}
              disabled={
                column.columnConfig === 'locked' ||
                (config?.visibility?.[id] !== false && countVisibleColumns <= 1)
              }
              className="w-4 h-4"
              onChange={(e) => {
                if (column.columnConfig !== 'locked') {
                  handleColumnVisibilityChange(id, e.target.checked);
                }
              }}
            />
            <KText
              textAlign="start"
              maxW="350px"
              minW="125px"
              pl="2px"
              w="auto"
              isTruncated
              enableTooltip={true}
              size="sm-semi-bold"
              color="secondary-700"
              cursor={
                column.columnConfig === 'locked' ||
                (config?.visibility?.[id] !== false && countVisibleColumns <= 1)
                  ? 'not-allowed'
                  : 'pointer'
              }
              onClick={() => {
                if (
                  column.columnConfig !== 'locked' &&
                  !(
                    config?.visibility?.[id] !== false &&
                    countVisibleColumns <= 1
                  )
                ) {
                  handleColumnVisibilityChange(id, !config?.visibility?.[id]);
                }
              }}
            >
              {displayTitle}
              {isGroup && (
                <KText as="span" size="xs" color="secondary-500" ml="4px">
                  ({subColumns?.length} columns)
                </KText>
              )}
            </KText>
          </Flex>
          <Divider w="110%" borderColor="border-color" m="0 -6px" />
        </Box>
      );
    };

    return (
      <DragDropContext onDragEnd={isReorderDisabled ? () => {} : onDragEnd}>
        <Droppable
          droppableId="table-column-droppable"
          isDropDisabled={isReorderDisabled}
        >
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={
                isReorderDisabled
                  ? {}
                  : getDroppableStyles(snapshot.isDraggingOver)
              }
            >
              {filteredColumns.map((column, index) => {
                const id = column.id || column.accessor;
                return (
                  <Draggable
                    key={id}
                    draggableId={id}
                    index={index}
                    isDragDisabled={isReorderDisabled}
                    disableInteractiveElementBlocking
                  >
                    {(provided, snapshot) =>
                      renderRow(column, provided, snapshot)
                    }
                  </Draggable>
                );
              })}
              <div
                style={{
                  minHeight: '8px',
                  transition: 'all 0.2s ease',
                }}
              >
                {provided.placeholder}
              </div>
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  }, [
    getOrderedConfigurableColumns,
    config.visibility,
    countVisibleColumns,
    onDragEnd,
    getDroppableStyles,
    getDraggableStyle,
    getDraggableSx,
    handleColumnVisibilityChange,
    tableColStyles,
  ]);

  // Memoize the default columns render to prevent unnecessary re-renders
  const renderDefaultColumns = useCallback(() => {
    return (
      <>
        {columns?.map((column) => {
          const { title = '', columnConfig = true } = column;

          if (columnConfig) {
            return null;
          }

          const columnKey =
            column.id || column.accessor || `column-${Math.random()}`;
          return (
            <>
              <Flex key={columnKey} p="12px 6px" gap="6px">
                <Dragsvg
                  size={16}
                  className="cursor-not-allowed mt-0.5"
                  opacity={0.3}
                />
                <Checkbox
                  className="w-4 h-4 cursor-not-allowed"
                  disabled
                  checked
                />

                <KText
                  textAlign="start"
                  maxW="350px"
                  minW="125px"
                  w="auto"
                  pl="2px"
                  isTruncated
                  enableTooltip={true}
                  size="sm-semi-bold"
                  color="secondary-700"
                >
                  {title}
                </KText>
              </Flex>
              <Divider w="110%" borderColor="border-color" m="0 -6px" />
            </>
          );
        })}
      </>
    );
  }, [columns]);

  const popoverRef = useRef(null);
  const popoverContentRef = useRef(null);

  useOutsideClick({
    ref: popoverRef,
    handler: (event) => {
      // Check if the click is inside the popover content
      if (
        popoverContentRef.current &&
        popoverContentRef.current.contains(event.target)
      ) {
        return;
      }
      onClose();
    },
  });

  const triggerPortalRef = tableColStyles?.triggerPortalRef as
    | React.RefObject<HTMLElement>
    | undefined;

  const content = (
    <Box
      ref={popoverRef}
      position="relative"
      {...(tableColStyles?.triggerContainer ?? {})}
    >
      <Popover
        isOpen={isOpen}
        modifiers={[
          {
            name: 'computeStyles',
            options: { gpuAcceleration: false, adaptive: false },
          },
        ]}
      >
        <PopoverTrigger>
          <Flex
            w="28px"
            h="28px"
            alignItems="center"
            justifyContent="center"
            borderTopLeftRadius="6px"
            borderBottomLeftRadius="6px"
            bgColor="primary-100"
            border="1px solid"
            borderColor="primary-200"
            cursor="pointer"
            position="absolute"
            top="3px"
            right="0px"
            zIndex="3"
            onClick={isOpen ? onClose : onOpen}
            {...(tableColStyles?.trigger ?? {})}
          >
            <Icon
              as={Settings01}
              color="primary-600"
              width="14px"
              height="14px"
              {...(tableColStyles?.triggerIcon ?? {})}
            />
          </Flex>
        </PopoverTrigger>
        <Portal appendToParentPortal={false}>
          <PopoverContent
            ref={popoverContentRef}
            minW="250px"
            maxW="400px"
            w="250px"
            maxH="400px"
            overflowY="hidden"
            p="0px"
            m="0px"
            right="15px"
            rootProps={
              tableColStyles?.popoverContent?.zIndex !== null
                ? { zIndex: tableColStyles?.popoverContent?.zIndex }
                : undefined
            }
            {...(tableColStyles?.popoverContent ?? {})}
          >
            <PopoverBody position="relative" m="0px" p="0px 6px" h="100%">
              {tableColStyles?.title && (
                <>
                  <KText
                    variant="heading_4"
                    borderBottom="1px solid"
                    borderColor="border-color"
                    p="12px"
                    m="0 -6px"
                    bg="secondary-25"
                  >
                    {tableColStyles?.title}
                  </KText>
                </>
              )}
              <Box
                overflowY="auto"
                overflowX="hidden"
                maxH={tableColStyles?.title ? 'calc(400px - 50px)' : '40vh'}
                {...(tableColStyles?.columnUiWrapper ?? {})}
              >
                {renderDefaultColumns()}
                {renderColumnSettings()}
              </Box>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    </Box>
  );

  if (triggerPortalRef) {
    return (
      <Portal containerRef={triggerPortalRef} appendToParentPortal={false}>
        {content}
      </Portal>
    );
  }
  return content;
};
